/** Admin: Earn program settings (tiers, points, quests, referral program) and Yield plans with early-unlock requests. */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  D,
  fmtDateTime,
  fmtUsd,
  badgeCatalog,
  MISSION_GROUP_LABEL,
  SEASON_BADGES,
  STORE_KIND_LABEL,
  STORE_KINDS,
  storeEffect,
  validateStoreItem,
  validateSeasonPrizes,
  rangeQuery,
  todayIn,
  type SeasonPrize,
  isTimedKind,
  MISSIONS,
  missionTitle,
  QUESTS,
  REFERRAL_FLAG_LABEL,
  termLabel,
  validateEarnSettings,
  YIELD_COIN_INFO,
  YIELD_COINS,
  YIELD_TERMS,
  type AdminSeason,
  type AdminStore,
  type StoreItem,
  type StoreItemInput,
  type StoreKind,
  type AdminSeasons,
  type EarnAdminOverview,
  type EarnAdminUser,
  type EarnProgram,
  type EarnSettings,
  type ReferralReview,
  type ReferralReviewStatus,
  type UnlockRequest,
  type YieldCoin,
  type YieldPlan,
} from '@platform/shared';
import { DateField, isCustom, periodRangeOf, periodText, TimeFrameSelect, type Period } from '../components/DateRange';
import { ClientCell, clientLabel } from '../components/ClientCell';
import { cx, Empty, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { UserVolumePanel, VolumeRewardsAdmin } from './AdminVolumeRewards';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

function useGet<T>(path: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (e) {
      setError(err(e));
    }
  }, [path]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, reload };
}

function Section({ title, sub, children, aside }: { title: string; sub?: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="m-0 text-sm font-semibold">{title}</h2>
          {sub && <p className="m-0 mt-0.5 text-xs text-muted">{sub}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Num({ value, onChange, label, suffix, step = 'any', className }: { value: number; onChange: (v: number) => void; label: string; suffix?: string; step?: string; className?: string }) {
  return (
    <label className={cx('flex min-w-0 flex-col gap-1 text-[11px] text-muted', className)}>
      {label}
      <span className="relative">
        <input type="number" step={step} value={Number.isFinite(value) ? value : ''} onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))} className={cx(input, 'num', suffix && 'pr-8')} />
        {suffix && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted">{suffix}</span>}
      </span>
    </label>
  );
}

// ---------- Earn program ----------

const EARN_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'missions', label: 'Missions' },
  { id: 'seasons', label: 'Seasons' },
  { id: 'store', label: 'Store' },
  { id: 'volume', label: 'Volume rewards' },
  { id: 'referrals', label: 'Referral program' },
  { id: 'reviews', label: 'Reward checks' },
  { id: 'tiers', label: 'Tiers, points & quests' },
  { id: 'history', label: 'History' },
] as const;
type EarnSection = (typeof EARN_SECTIONS)[number]['id'];

function savedSection(): EarnSection {
  const v = new URLSearchParams(window.location.search).get('section');
  return EARN_SECTIONS.some((x) => x.id === v) ? (v as EarnSection) : 'overview';
}

function Kpi({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-line bg-panel p-3.5">
      <span className="text-[11px] text-muted">{label}</span>
      <span className="num text-xl font-semibold">{value}</span>
      {sub && <span className="text-[11px] text-muted">{sub}</span>}
    </div>
  );
}

const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');

/** Period for the KPIs that count activity (UTC days); the last 30 days is the server's default. */
const KPI_PERIODS: { id: number; label: string }[] = [
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
  { id: 365, label: '12 months' },
];

function useKpiPeriod() {
  const [period, setPeriod] = useState<Period<number>>({ id: 30 });
  const range = periodRangeOf(period, (d) => d, 'utc');
  const qs = period.id === 30 || !range ? '' : `?${rangeQuery(range, 'utc')}`;
  const label = isCustom(period) ? periodText(period, KPI_PERIODS) : `last ${periodText(period, KPI_PERIODS)}`;
  const picker = <TimeFrameSelect label="KPI period" zone="utc" presets={KPI_PERIODS} value={period} onChange={setPeriod} />;
  return { qs, label, picker };
}

/** Program-wide numbers: who takes part, what's been paid, quest completion and referral conversion. */
function EarnOverview({ settings, onReviews }: { settings: EarnSettings; onReviews: () => void }) {
  const kpi = useKpiPeriod();
  const { data, error } = useGet<EarnAdminOverview>(`/api/admin/earn/overview${kpi.qs}`);
  if (!data) return error ? <p className="text-sm text-down">{error}</p> : <Spinner />;
  const f = data.referrals30d;
  return (
    <div className="flex flex-col gap-4">
      {data.reviewsHeld > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-warn/40 bg-warn-bg px-4 py-3 text-xs">
          <span className="min-w-0 flex-1 text-fg">
            <b className="text-warn">
              {data.reviewsHeld} referral {data.reviewsHeld === 1 ? 'reward is' : 'rewards are'} on hold
            </b>{' '}
            · the friend shares an IP, name, phone or payout account with the person who invited them. Nothing is paid until you decide.
          </span>
          <button type="button" className={btn} onClick={onReviews}>
            Review now
          </button>
        </div>
      )}
      <div className="flex items-center justify-end gap-2 text-xs text-muted">
        Claims and referral sign-ups for {kpi.picker}
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Clients with points" value={data.ranked.toLocaleString('en-US')} sub={`${pct(data.ranked, data.clients)} of ${data.clients.toLocaleString('en-US')} active clients`} />
        <Kpi
          label="Points held"
          value={data.points.total.toLocaleString('en-US')}
          sub={`Trading ${pct(data.points.trading, data.points.total)} · Quests ${pct(data.points.quests, data.points.total)} · Missions ${pct(data.points.missions, data.points.total)} · Friends ${pct(data.points.referral, data.points.total)} · Tiers ${pct(data.points.tiers, data.points.total)} · Seasons ${pct(data.points.seasons, data.points.total)}`}
        />
        <Kpi label={`Claimed, ${kpi.label}`} value={fmtUsd(D.from(data.claims30d.cashback).add(data.claims30d.referral).toString())} sub={`Cashback ${fmtUsd(data.claims30d.cashback)} · Referral ${fmtUsd(data.claims30d.referral)}`} />
        <Kpi label={`Referred sign-ups → first trade, ${kpi.label}`} value={pct(f.traded, f.joined)} sub={`${f.joined} joined · ${f.verified} verified · ${f.traded} traded`} />
      </div>
      <MissionsWeek data={data} settings={settings} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Section title="Quests" sub="Active clients who finished each quest. Starter steps are shown to clients in this order.">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] border-collapse text-xs">
              <thead>
                <tr>
                  {['Quest', 'Kind', 'Points', 'Done', 'Rate'].map((h) => (
                    <th key={h} className={cx(th, h !== 'Quest' && h !== 'Kind' && 'text-right')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {QUESTS.map((qd) => {
                  const done = data.quests.find((x) => x.id === qd.id)?.done ?? 0;
                  return (
                    <tr key={qd.id} className="border-t border-line">
                      <td className={td}>{qd.title}</td>
                      <td className={cx(td, 'text-muted')}>{qd.starter ? 'Starter' : 'Milestone'}</td>
                      <td className={cx(td, 'num text-right')}>{(settings.questPoints[qd.id] ?? qd.points).toLocaleString('en-US')}</td>
                      <td className={cx(td, 'num text-right')}>{done.toLocaleString('en-US')}</td>
                      <td className={cx(td, 'num text-right')}>{pct(done, data.clients)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
        <div className="flex flex-col gap-4">
          <Section title="Clients per tier">
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-xs">
              {settings.tiers.map((t) => {
                const c = data.tiers[t.id] ?? 0;
                return (
                  <li key={t.id} className="flex items-center gap-2">
                    <span className="grid size-5 place-items-center rounded text-[10px] font-bold text-black" style={{ background: t.color }}>
                      {t.name[0]}
                    </span>
                    <span className="w-20">{t.name}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-track">
                      <span className="block h-full rounded-full" style={{ width: pct(c, data.clients) === '—' ? '0%' : pct(c, data.clients), background: t.color }} />
                    </span>
                    <span className="num w-12 text-right">{c.toLocaleString('en-US')}</span>
                  </li>
                );
              })}
            </ul>
          </Section>
          <Section title="Top referrers" sub="All time, by friends who joined">
            {data.topReferrers.length === 0 ? (
              <p className="m-0 text-xs text-muted">Nobody has referred a friend yet.</p>
            ) : (
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className={th}>Username</th>
                    <th className={cx(th, 'text-right')}>Joined</th>
                    <th className={cx(th, 'text-right')}>Traded</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topReferrers.map((r) => (
                    <tr key={r.username} className="border-t border-line">
                      <td className={td}>{r.username}</td>
                      <td className={cx(td, 'num text-right')}>{r.friends}</td>
                      <td className={cx(td, 'num text-right')}>{r.traded}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

/** This week's missions: how many clients got each one and how many finished it. */
function MissionsWeek({ data, settings }: { data: EarnAdminOverview; settings: EarnSettings }) {
  const rows = data.missionsWeek.filter((r) => r.assigned > 0);
  return (
    <Section title="This week's missions" sub="Clients get their missions the first time their week is checked: when they trade, or open Earn.">
      {rows.length === 0 ? (
        <p className="m-0 text-xs text-muted">No client has missions this week yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] border-collapse text-xs">
            <thead>
              <tr>
                {['Mission', 'Group', 'Got it', 'Finished', 'Rate'].map((h) => (
                  <th key={h} className={cx(th, h !== 'Mission' && h !== 'Group' && 'text-right')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const m = MISSIONS.find((x) => x.id === r.id)!;
                return (
                  <tr key={r.id} className="border-t border-line">
                    <td className={td}>{missionTitle(m, settings.missions.pool[m.id].target)}</td>
                    <td className={cx(td, 'text-muted')}>{MISSION_GROUP_LABEL[m.group]}</td>
                    <td className={cx(td, 'num text-right')}>{r.assigned}</td>
                    <td className={cx(td, 'num text-right')}>{r.done}</td>
                    <td className={cx(td, 'num text-right')}>{pct(r.done, r.assigned)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ---------- Seasons ----------

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const fromLocalInput = (v: string) => new Date(v).toISOString();
const STATUS_CLS: Record<AdminSeason['status'], string> = { live: 'bg-up-bg text-up', upcoming: 'bg-accent-bg text-accent-fg', ended: 'bg-raised text-muted' };

function PrizeTable({ prizes, onChange }: { prizes: SeasonPrize[]; onChange: (p: SeasonPrize[]) => void }) {
  const names = badgeCatalog([]);
  const up = (i: number, patch: Partial<SeasonPrize>) => onChange(prizes.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-xs">
          <thead>
            <tr>
              {['From rank', 'To rank', 'Points', 'Badge', ''].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prizes.map((p, i) => (
              <tr key={i} className="border-t border-line">
                <td className={td}>
                  <input aria-label="From rank" type="number" step="1" value={p.fromRank} onChange={(e) => up(i, { fromRank: Number(e.target.value) })} className={cx(input, 'num w-24')} />
                </td>
                <td className={td}>
                  <input aria-label="To rank" type="number" step="1" value={p.toRank} onChange={(e) => up(i, { toRank: Number(e.target.value) })} className={cx(input, 'num w-24')} />
                </td>
                <td className={td}>
                  <input aria-label="Points" type="number" step="1" value={p.points} onChange={(e) => up(i, { points: Number(e.target.value) })} className={cx(input, 'num w-28')} />
                </td>
                <td className={td}>
                  <select aria-label="Badge" value={p.badge ?? ''} onChange={(e) => up(i, { badge: (e.target.value || null) as SeasonPrize['badge'] })} className={cx(input, 'w-44')}>
                    <option value="">No badge</option>
                    {SEASON_BADGES.map((b) => (
                      <option key={b} value={b}>
                        {names.find((x) => x.id === b)?.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className={td}>
                  <button type="button" className={btn} onClick={() => onChange(prizes.filter((_, k) => k !== i))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className={cx(btn, 'self-start')}
        disabled={prizes.length >= 20}
        onClick={() => {
          const last = prizes[prizes.length - 1];
          const from = last ? last.toRank + 1 : 1;
          onChange([...prizes, { fromRank: from, toRank: from, points: 0, badge: null }]);
        }}
      >
        + Add rank range
      </button>
    </div>
  );
}

function SeasonEditor({ s, onSaved }: { s: AdminSeason; onSaved: () => void }) {
  const [name, setName] = useState(s.name);
  const [starts, setStarts] = useState(toLocalInput(s.startsAt));
  const [ends, setEnds] = useState(toLocalInput(s.endsAt));
  const [prizes, setPrizes] = useState<SeasonPrize[]>(structuredClone(s.prizes));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const problem = validateSeasonPrizes(prizes);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(`/api/admin/earn/seasons/${s.id}`, { name, endsAt: fromLocalInput(ends), prizes, ...(s.status === 'upcoming' ? { startsAt: fromLocalInput(starts) } : {}) });
      setMsg({ ok: true, text: 'Saved.' });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      title={`${s.name}${s.status === 'live' ? ' (running)' : ' (next)'}`}
      sub={s.status === 'live' ? `${s.ranked.toLocaleString('en-US')} clients with season points so far. Prizes are paid automatically when it ends, and the next season starts straight away.` : 'Starts when the running season ends.'}
      aside={<span className={cx('rounded px-2 py-0.5 text-xs font-semibold', STATUS_CLS[s.status])}>{s.status === 'live' ? 'Live' : 'Upcoming'}</span>}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Name
          <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} className={input} />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Starts (your time)
          <DateField withTime aria-label="Starts" value={starts} disabled={s.status !== 'upcoming'} min={s.status === 'upcoming' ? todayIn() : undefined} onChange={setStarts} className={input} />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Ends (your time)
          <DateField withTime aria-label="Ends" value={ends} onChange={setEnds} className={input} />
        </label>
      </div>
      <PrizeTable prizes={prizes} onChange={setPrizes} />
      <div className="flex flex-wrap items-center justify-end gap-3">
        {problem && <span className="text-xs text-down">{problem}</span>}
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        <button type="button" className={primary} disabled={busy || !!problem} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save season'}
        </button>
      </div>
    </Section>
  );
}

function ScheduleNext({ live, weeks, onSaved }: { live: AdminSeason; weeks: number; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [len, setLen] = useState(weeks);
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  return (
    <Section title="Next season" sub={`If you don't schedule one, the next season starts when ${live.name} ends, with the same length and prizes.`}>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Name (optional)
          <input value={name} maxLength={40} placeholder="Season name" onChange={(ev) => setName(ev.target.value)} className={cx(input, 'w-56')} />
        </label>
        <Num label="Length" suffix="weeks" step="1" value={len} onChange={setLen} className="w-36" />
        <button
          type="button"
          className={primary}
          disabled={busy || !(len >= 1 && len <= 52)}
          onClick={async () => {
            setBusy(true);
            setE(null);
            try {
              await api.post('/api/admin/earn/seasons', { name, weeks: len, prizes: live.prizes });
              onSaved();
            } catch (ex) {
              setE(err(ex));
            } finally {
              setBusy(false);
            }
          }}
        >
          Schedule next season
        </button>
        {e && <span className="pb-2 text-xs text-down">{e}</span>}
      </div>
      <p className="m-0 text-xs text-muted">It starts when {live.name} ends and uses its prizes; you can change them once it's scheduled.</p>
    </Section>
  );
}

function SeasonsAdmin() {
  const { data, error, reload } = useGet<AdminSeasons>('/api/admin/earn/seasons');
  if (!data) return error ? <p className="text-sm text-down">{error}</p> : <Spinner />;
  const live = data.seasons.find((s) => s.status === 'live');
  const next = data.seasons.find((s) => s.status === 'upcoming');
  return (
    <div className="flex flex-col gap-4">
      {live && <SeasonEditor key={`${live.id}-${live.endsAt}`} s={live} onSaved={() => void reload()} />}
      {next ? <SeasonEditor key={`${next.id}-${next.endsAt}`} s={next} onSaved={() => void reload()} /> : live && <ScheduleNext live={live} weeks={data.defaultWeeks} onSaved={() => void reload()} />}
      <Section title="All seasons">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-xs">
            <thead>
              <tr>
                {['Season', 'Dates', 'Status', 'Ranked', 'Winner', 'Prizes'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.seasons.map((s) => (
                <tr key={s.id} className="border-t border-line">
                  <td className={td}>{s.name}</td>
                  <td className={cx(td, 'num text-muted')}>
                    {fmtDateTime(s.startsAt, { year: true })} – {fmtDateTime(s.endsAt, { year: true })}
                  </td>
                  <td className={td}>
                    <span className={cx('rounded px-2 py-0.5 font-semibold', STATUS_CLS[s.status])}>{s.status === 'live' ? 'Live' : s.status === 'upcoming' ? 'Upcoming' : 'Ended'}</span>
                  </td>
                  <td className={cx(td, 'num')}>{s.ranked.toLocaleString('en-US')}</td>
                  <td className={td}>{s.winner ?? '—'}</td>
                  <td className={cx(td, 'text-muted')}>{s.status === 'ended' ? (s.paidAt ? 'Paid' : 'Paying…') : `${s.prizes.length} rank ranges`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

// ---------- Store ----------

const BLANK_ITEM: StoreItemInput = { kind: 'cash', name: '', description: '', params: { usd: 10 }, price: 10_000, stock: null, limitCount: 1, limitDays: 30, excludedCountries: [], enabled: false, sort: 100 };

function ItemEditor({ item, onClose, onSaved }: { item: StoreItem | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<StoreItemInput>(() => (item ? structuredClone(item) : structuredClone(BLANK_ITEM)));
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  const up = (patch: Partial<StoreItemInput>) => setF((cur) => ({ ...cur, ...patch }));
  const timed = isTimedKind(f.kind);
  const problem = validateStoreItem(f);
  const locked = !!item && item.sold > 0;
  return (
    <Modal open onClose={onClose} title={item ? `Edit ${item.name}` : 'Add a reward'} width={560}>
      <div className="flex flex-col gap-3 text-xs">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            What it does
            <select
              value={f.kind}
              disabled={locked}
              onChange={(ev) => {
                const kind = ev.target.value as StoreKind;
                up({ kind, params: kind === 'cash' ? { usd: 10 } : isTimedKind(kind) ? { pct: 10, hours: 24 } : {} });
              }}
              className={input}
            >
              {STORE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {STORE_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Name
            <input value={f.name} maxLength={60} onChange={(ev) => up({ name: ev.target.value })} className={input} />
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-[11px] text-muted">
            Description (shown to clients)
            <input value={f.description} maxLength={200} placeholder={storeEffect(f.kind, f.params)} onChange={(ev) => up({ description: ev.target.value })} className={input} />
          </label>
          {timed && (
            <>
              <Num label={f.kind === 'fee_discount' ? 'Share of fees paid back' : f.kind === 'spread_discount' ? 'Share of spread paid back' : 'Extra cashback'} suffix="%" value={f.params.pct ?? NaN} onChange={(v) => up({ params: { ...f.params, pct: v } })} />
              <Num label="Runs for" suffix="hours" step="1" value={f.params.hours ?? NaN} onChange={(v) => up({ params: { ...f.params, hours: v } })} />
            </>
          )}
          {f.kind === 'cash' && <Num label="Amount" suffix="$" value={f.params.usd ?? NaN} onChange={(v) => up({ params: { usd: v } })} />}
          <Num label="Price" suffix="pts" step="1" value={f.price} onChange={(v) => up({ price: v })} />
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Stock (empty = unlimited)
            <input type="number" step="1" value={f.stock ?? ''} onChange={(ev) => up({ stock: ev.target.value === '' ? null : Number(ev.target.value) })} className={cx(input, 'num')} />
          </label>
          <Num label="Per client" step="1" value={f.limitCount} onChange={(v) => up({ limitCount: v })} />
          <Num label="…every" suffix="days" step="1" value={f.limitDays} onChange={(v) => up({ limitDays: v })} />
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Not for countries
            <CountriesInput value={f.excludedCountries} onChange={(v) => up({ excludedCountries: v })} />
          </label>
          <Num label="Order" step="1" value={f.sort} onChange={(v) => up({ sort: v })} />
          <label className="col-span-2 flex items-center gap-2 text-fg">
            <input type="checkbox" checked={f.enabled} onChange={(ev) => up({ enabled: ev.target.checked })} className="size-4 accent-[var(--accent)]" />
            On sale
          </label>
        </div>
        <p className="m-0 text-muted">
          {locked ? "People have bought this, so what it does can't change. " : ''}Changes apply to new purchases only. Money rewards are paid as cashback the client claims.
        </p>
        {(e || problem) && <p className="m-0 text-down">{e ?? problem}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primary}
            disabled={busy || !!problem}
            onClick={async () => {
              setBusy(true);
              setE(null);
              try {
                if (item) await api.put(`/api/admin/earn/store/items/${item.id}`, f);
                else await api.post('/api/admin/earn/store/items', f);
                onSaved();
                onClose();
              } catch (ex) {
                setE(err(ex));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StoreAdmin() {
  const kpi = useKpiPeriod();
  const { data, error, reload } = useGet<AdminStore>(`/api/admin/earn/store${kpi.qs}`);
  const [editing, setEditing] = useState<StoreItem | 'new' | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (!data) return error ? <p className="text-sm text-down">{error}</p> : <Spinner />;
  const refund = async (id: string, label: string) => {
    if (!window.confirm(`Refund ${label}? The points go back and the reward stops.`)) return;
    setMsg(null);
    try {
      await api.post(`/api/admin/earn/store/redemptions/${id}/refund`);
      setMsg({ ok: true, text: 'Refunded.' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2 text-xs text-muted">
        Redemptions, points and cash items for {kpi.picker}
      </div>
      <div className="grid gap-2.5 sm:grid-cols-3">
        <Kpi label={`Redemptions, ${kpi.label}`} value={data.stats.redemptions.toLocaleString('en-US')} />
        <Kpi label={`Points spent, ${kpi.label}`} value={data.stats.points.toLocaleString('en-US')} />
        <Kpi label={`Cash items, ${kpi.label}`} value={fmtUsd(data.stats.usd)} sub="Fee discounts and boosts pay on top, from fees" />
      </div>
      <Section
        title="Rewards"
        sub="Clients see items that are on sale and not excluded for their country. Fee discounts and boosts pay back a share of live fees as cashback; nothing changes the fees charged."
        aside={
          <button type="button" className={primary} onClick={() => setEditing('new')}>
            Add reward
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-xs">
            <thead>
              <tr>
                {['Reward', 'Does', 'Price', 'Stock', 'Per client', 'Not for', 'Sold', 'On sale', ''].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className={cx('border-t border-line', !i.enabled && 'opacity-60')}>
                  <td className={td}>{i.name}</td>
                  <td className={cx(td, 'max-w-[260px] truncate text-muted')} title={storeEffect(i.kind, i.params)}>
                    {storeEffect(i.kind, i.params)}
                  </td>
                  <td className={cx(td, 'num')}>{i.price.toLocaleString('en-US')}</td>
                  <td className={cx(td, 'num')}>{i.stock === null ? '∞' : `${Math.max(0, i.stock - i.sold)} of ${i.stock}`}</td>
                  <td className={cx(td, 'num')}>
                    {i.limitCount} / {i.limitDays}d
                  </td>
                  <td className={cx(td, 'num text-muted')}>{i.excludedCountries.join(', ') || '—'}</td>
                  <td className={cx(td, 'num')}>{i.sold}</td>
                  <td className={td}>{i.enabled ? <span className="rounded bg-up-bg px-1.5 text-up">On</span> : <span className="text-muted">Off</span>}</td>
                  <td className={td}>
                    <button type="button" className={btn} onClick={() => setEditing(i)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Recent redemptions" sub="Refunding gives the points back and cancels the reward. It's refused once the reward was used or its cashback claimed.">
        {msg && <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
        {data.redemptions.length === 0 ? (
          <p className="m-0 text-xs text-muted">Nothing redeemed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse text-xs">
              <thead>
                <tr>
                  {['When', 'Client', 'Reward', 'Points', 'Status', ''].map((h) => (
                    <th key={h} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.redemptions.map((r) => {
                  const used = r.usedAt || r.usedWeek;
                  return (
                    <tr key={r.id} className="border-t border-line">
                      <td className={cx(td, 'num text-muted')}>{fmtDateTime(r.createdAt, { year: true })}</td>
                      <td className={td}>{r.username}</td>
                      <td className={td}>{r.name}</td>
                      <td className={cx(td, 'num')}>{r.points.toLocaleString('en-US')}</td>
                      <td className={td}>
                        {r.status === 'refunded' ? (
                          <span className="text-muted">Refunded</span>
                        ) : used ? (
                          <span className="text-muted">Used</span>
                        ) : r.endsAt && Date.parse(r.endsAt) > Date.now() ? (
                          <span className="rounded bg-up-bg px-1.5 text-up">Running</span>
                        ) : (
                          <span>Active</span>
                        )}
                      </td>
                      <td className={td}>
                        {r.status === 'active' && !used && (
                          <button type="button" className={cx(btn, 'text-down')} onClick={() => void refund(r.id, `${r.name} for ${r.username}`)}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {editing && <ItemEditor item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => void reload()} />}
    </div>
  );
}

/** Comma-separated country codes; kept as typed until the field loses focus. */
function CountriesInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [raw, setRaw] = useState<string | null>(null);
  return (
    <input
      aria-label="Countries that never get this mission"
      placeholder="None"
      value={raw ?? value.join(', ')}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        if (raw === null) return;
        onChange(raw.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean));
        setRaw(null);
      }}
      className={cx(input, 'num w-36 uppercase placeholder:normal-case')}
    />
  );
}

/** Admin Users detail: the client's tier, points, quests and unclaimed rewards. Read-only. */
const REVIEW_FILTERS: { id: ReferralReviewStatus | 'all'; label: string }[] = [
  { id: 'held', label: 'On hold' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];
const REVIEW_CLS: Record<ReferralReviewStatus, string> = { held: 'bg-warn-bg text-warn', approved: 'bg-up-bg text-up', rejected: 'bg-down-bg text-down' };

function ReviewDecide({ r, decision, onClose, onDone }: { r: ReferralReview; decision: 'approved' | 'rejected'; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} title={decision === 'approved' ? 'Approve referral reward' : 'Reject referral reward'} width={440}>
      <div className="flex flex-col gap-3 text-xs">
        <p className="m-0 text-muted">
          {decision === 'approved'
            ? `${r.referrer} can claim the ${fmtUsd(r.bonusUsd)} reward for ${r.friend}, and the friend counts toward their cash milestones.`
            : `${r.referrer} isn't paid for ${r.friend} and the friend doesn't count toward milestones. Their share of the friend's fees is not affected.`}{' '}
          This can't be changed later.
        </p>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Note for the audit log {decision === 'rejected' ? '(required)' : '(optional)'}
          <textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={3} maxLength={500} className={cx(input, 'h-auto py-2')} />
        </label>
        {e && <p className="m-0 text-down">{e}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || (decision === 'rejected' && !note.trim())}
            className={cx('h-9 rounded-lg px-4 text-xs font-semibold text-white disabled:opacity-50', decision === 'approved' ? 'bg-accent' : 'bg-btn-short')}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/api/admin/earn/referral-reviews/${r.friendId}`, { decision, note });
                onDone();
              } catch (x) {
                setE(err(x));
                setBusy(false);
              }
            }}
          >
            {decision === 'approved' ? 'Approve reward' : 'Reject reward'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Referral rewards held by the automatic checks, waiting for an admin to approve or reject. */
function ReviewQueue() {
  const [filter, setFilter] = useState<ReferralReviewStatus | 'all'>('held');
  const { data, error, reload } = useGet<ReferralReview[]>(`/api/admin/earn/referral-reviews?status=${filter}`);
  const [deciding, setDeciding] = useState<{ r: ReferralReview; decision: 'approved' | 'rejected' } | null>(null);
  return (
    <Section
      title="Referral reward checks"
      sub="When a friend qualifies, they're checked once against the person who invited them. A shared IP, name, phone number or payout account holds the sign-up reward here until you decide. Fee shares keep being paid either way."
      aside={
        <div className="flex gap-1 rounded-lg bg-track p-0.5">
          {REVIEW_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={cx('rounded-md px-2.5 py-1 text-[12px] font-semibold', filter === f.id ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
            >
              {f.label}
            </button>
          ))}
        </div>
      }
    >
      {!data ? (
        error ? (
          <p className="m-0 text-xs text-down">{error}</p>
        ) : (
          <Spinner />
        )
      ) : data.length === 0 ? (
        <Empty>{filter === 'held' ? 'Nothing on hold. Rewards that pass the checks are paid without a review.' : 'Nothing here yet.'}</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] border-collapse text-xs">
            <thead>
              <tr>
                {['Friend', 'Invited by', 'Why it was held', 'Reward', 'Deposited', 'Checked', 'Status', ''].map((h, i) => (
                  <th key={i} className={cx(th, (h === 'Reward' || h === 'Deposited') && 'text-right')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.friendId} className="border-t border-line align-top">
                  <td className={td}>{r.friend}</td>
                  <td className={td}>{r.referrer}</td>
                  <td className={cx(td, 'whitespace-normal')}>
                    <span className="flex flex-wrap gap-1">
                      {r.reasons.map((x) => (
                        <span key={x} className="rounded bg-warn-bg px-1.5 py-0.5 text-[11px] font-medium text-warn">
                          {REFERRAL_FLAG_LABEL[x]}
                        </span>
                      ))}
                    </span>
                    {r.note && <span className="mt-1 block max-w-[260px] text-muted">“{r.note}”</span>}
                  </td>
                  <td className={cx(td, 'num text-right')}>{fmtUsd(r.bonusUsd)}</td>
                  <td className={cx(td, 'num text-right')}>{fmtUsd(r.deposited)}</td>
                  <td className={cx(td, 'text-muted')}>{fmtDateTime(r.checkedAt)}</td>
                  <td className={td}>
                    <span className={cx('rounded px-1.5 py-0.5 text-[11px] font-semibold', REVIEW_CLS[r.status])}>{REVIEW_FILTERS.find((f) => f.id === r.status)?.label}</span>
                    {r.decidedBy && (
                      <span className="mt-1 block text-muted">
                        {r.decidedBy} · {r.decidedAt && fmtDateTime(r.decidedAt)}
                      </span>
                    )}
                  </td>
                  <td className={cx(td, 'text-right')}>
                    {r.status === 'held' && (
                      <span className="inline-flex gap-1.5">
                        <button type="button" className={btn} onClick={() => setDeciding({ r, decision: 'approved' })}>
                          Approve
                        </button>
                        <button type="button" className={cx(btn, 'text-down')} onClick={() => setDeciding({ r, decision: 'rejected' })}>
                          Reject
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {deciding && (
        <ReviewDecide
          {...deciding}
          onClose={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            void reload();
          }}
        />
      )}
    </Section>
  );
}

export function UserEarnPanel({ userId }: { userId: string }) {
  const { data, error } = useGet<EarnAdminUser>(`/api/admin/earn/users/${userId}`);
  if (!data) return error ? <p className="m-0 text-xs text-down">{error}</p> : <Spinner />;
  const done = data.quests.filter((x) => x.done).length;
  return (
    <>
      <div className="flex flex-col gap-2 rounded-lg border border-line p-3 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-sm">Rewards</b>
          <span className="text-muted">@{data.username}</span>
          <span className="rounded bg-raised px-2 py-0.5">{data.tierId}</span>
          {data.referredBy && <span className="text-muted">Joined with code {data.referredBy}</span>}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(
            [
              ['Points', data.points.total.toLocaleString('en-US')],
              ['Rank', data.rank ? `#${data.rank}` : '—'],
              ['Quests', `${done} of ${data.quests.length}`],
              ['Friends', String(data.referrals)],
              ['Live volume', fmtUsd(data.volume)],
              ['Cashback unclaimed', fmtUsd(data.cashback.claimable)],
              ['Referral unclaimed', fmtUsd(data.referral.claimable)],
              ['Claimed all time', fmtUsd(D.from(data.cashback.claimed).add(data.referral.claimed).toString())],
            ] as const
          ).map(([l, v]) => (
            <div key={l} className="rounded-md bg-raised px-2.5 py-1.5">
              <div className="text-[10.5px] text-muted">{l}</div>
              <div className="num font-semibold">{v}</div>
            </div>
          ))}
        </div>
      </div>
      <UserVolumePanel userId={userId} />
    </>
  );
}

export function EarnSettingsTab() {
  const { data, error, reload } = useGet<EarnProgram & { history: { id: number; effectiveFrom: string; by: string | null }[] }>('/api/admin/earn/settings');
  const [s, setS] = useState<EarnSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [section, setSection] = useState<EarnSection>(savedSection);
  useEffect(() => {
    if (data) setS(structuredClone(data.settings));
  }, [data]);
  if (!s || !data) return error ? <p className="text-sm text-down">{error}</p> : <Spinner />;

  const set = (fn: (d: EarnSettings) => void) => setS((cur) => {
    const d = structuredClone(cur!);
    fn(d);
    return d;
  });
  const problem = validateEarnSettings(s);
  const dirty = JSON.stringify(s) !== JSON.stringify(data.settings);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/earn/settings', { settings: s });
      setMsg({ ok: true, text: 'Saved. The new settings apply to trades from now on.' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };

  const pick = (id: EarnSection) => {
    setSection(id);
    const u = new URL(window.location.href);
    u.searchParams.set('section', id);
    window.history.replaceState({}, '', u);
  };
  const editing = section === 'referrals' || section === 'tiers' || section === 'missions';

  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label="Earn program" className="no-scrollbar flex gap-1 overflow-x-auto rounded-xl bg-track p-1">
        {EARN_SECTIONS.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={section === x.id}
            onClick={() => pick(x.id)}
            className={cx('shrink-0 rounded-lg px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap', section === x.id ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
          >
            {x.label}
            {dirty && (x.id === 'referrals' || x.id === 'tiers' || x.id === 'missions') && <span className="ml-1.5 text-warn">•</span>}
          </button>
        ))}
      </div>

      {section === 'overview' && <EarnOverview settings={data.settings} onReviews={() => pick('reviews')} />}
      {section === 'reviews' && <ReviewQueue />}
      {section === 'seasons' && <SeasonsAdmin />}
      {section === 'store' && <StoreAdmin />}
      {section === 'volume' && <VolumeRewardsAdmin />}

      {editing && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel/95 px-4 py-3 backdrop-blur">
          <div className="min-w-0 flex-1 text-xs">
            <b className="text-sm">Earn program</b>
            <div className="text-muted">Changes apply to trades made after you save. Earnings already made keep the old rates.</div>
          </div>
          {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
          {problem && <span className="text-xs text-down">{problem}</span>}
          <button type="button" className={btn} disabled={!dirty || busy} onClick={() => setS(structuredClone(data.settings))}>
            Discard
          </button>
          <button type="button" className={primary} disabled={!dirty || !!problem || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      {section === 'missions' && (
        <>
          <Section
            title="Mission pool"
            sub="Each week a client gets one mission from each group, picked by weight, never one they had last week when the group has another. Changes apply from the next Monday: a week keeps the settings it started with."
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] border-collapse text-xs">
                <thead>
                  <tr>
                    {['On', 'Mission', 'Group', 'Target', 'Points', 'Weight', 'Not for countries'].map((h) => (
                      <th key={h} className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MISSIONS.map((m) => {
                    const c = s.missions.pool[m.id];
                    return (
                      <tr key={m.id} className={cx('border-t border-line', !c.enabled && 'opacity-60')}>
                        <td className={td}>
                          <input
                            type="checkbox"
                            aria-label={`${missionTitle(m, c.target)} on`}
                            checked={c.enabled}
                            onChange={(e) => set((d) => void (d.missions.pool[m.id].enabled = e.target.checked))}
                            className="size-4 accent-[var(--accent)]"
                          />
                        </td>
                        <td className={cx(td, 'max-w-[280px] truncate')} title={missionTitle(m, c.target)}>
                          {missionTitle(m, c.target)}
                        </td>
                        <td className={cx(td, 'text-muted')}>{MISSION_GROUP_LABEL[m.group]}</td>
                        <td className={td}>
                          <input aria-label="Target" type="number" step={m.unit === 'usd' ? 'any' : '1'} value={c.target} onChange={(e) => set((d) => void (d.missions.pool[m.id].target = Number(e.target.value)))} className={cx(input, 'num w-28')} />
                        </td>
                        <td className={td}>
                          <input aria-label="Points" type="number" step="1" value={c.points} onChange={(e) => set((d) => void (d.missions.pool[m.id].points = Number(e.target.value)))} className={cx(input, 'num w-24')} />
                        </td>
                        <td className={td}>
                          <input aria-label="Weight" type="number" step="1" value={c.weight} onChange={(e) => set((d) => void (d.missions.pool[m.id].weight = Number(e.target.value)))} className={cx(input, 'num w-20')} />
                        </td>
                        <td className={td}>
                          <CountriesInput value={c.excludedCountries} onChange={(v) => set((d) => void (d.missions.pool[m.id].excludedCountries = v))} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="m-0 text-xs text-muted">
              Countries are 2-letter codes from the client's profile, such as GB or DE. Missions that reward trading more should stay off in any country where compliance hasn't approved them.
            </p>
          </Section>
          <Section title="Streak and limits">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Num label="Missions a week for the streak" step="1" value={s.missions.streakNeeded} onChange={(v) => set((d) => void (d.missions.streakNeeded = v))} />
              <Num label="Streak bonus per week" suffix="%" value={s.missions.streakBonusPct} onChange={(v) => set((d) => void (d.missions.streakBonusPct = v))} />
              <Num label="Highest streak bonus" suffix="%" value={s.missions.streakMaxPct} onChange={(v) => set((d) => void (d.missions.streakMaxPct = v))} />
              <Num label="Mission points cap per week" step="1" value={s.missions.weeklyCap} onChange={(v) => set((d) => void (d.missions.weeklyCap = v))} />
              <Num label="Minimum hold time" suffix="s" step="1" value={s.missions.minHoldSeconds} onChange={(v) => set((d) => void (d.missions.minHoldSeconds = v))} />
            </div>
            <p className="m-0 text-xs text-muted">
              The streak bonus is a percentage of the week's mission points for each week in a row, paid when a week that kept the streak ends. Positions closed sooner than the minimum hold time don't count toward missions.
            </p>
          </Section>
        </>
      )}

      {section === 'referrals' && (
        <Section title="Referral program" sub="What clients earn for inviting friends. Shown on the Referrals page and used by the earnings calculator.">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Num label="Sign-up reward per friend" suffix="$" value={s.referral.bonusUsd} onChange={(v) => set((d) => void (d.referral.bonusUsd = v))} />
            {[0, 1, 2].map((i) => (
              <Num key={i} label={`Level ${i + 1} share of fees`} suffix="%" value={s.referral.levelPcts[i]!} onChange={(v) => set((d) => void (d.referral.levelPcts[i] = v))} />
            ))}
            <Num label="Referral points" suffix="%" value={s.referral.pointsPct} onChange={(v) => set((d) => void (d.referral.pointsPct = v))} />
            <Num label="Days to enter a code" step="1" value={s.referral.applyWindowDays} onChange={(v) => set((d) => void (d.referral.applyWindowDays = v))} />
          </div>
          <p className="m-0 text-xs text-muted">
            The sign-up reward is paid once per level 1 friend after they're verified and make a first live trade. Level shares are a percentage of the trading fees each referred user pays. Referral points are a percentage of
            level 1 friends' trading points.
          </p>
        </Section>
      )}
      {section === 'referrals' && (
        <Section
          title="Cash milestones"
          sub="One-off cash rewards when a client's level 1 friends reach a count. Only friends whose sign-up reward was paid count, so held or rejected friends don't. Each milestone pays the amount in effect when it was reached."
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[380px] border-collapse text-xs">
              <thead>
                <tr>
                  <th className={th}>Friends rewarded</th>
                  <th className={th}>Cash reward</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {s.referral.milestones.map((m, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className={td}>
                      <input aria-label="Friends rewarded" type="number" step="1" value={m.friends} onChange={(e) => set((d) => void (d.referral.milestones[i]!.friends = Number(e.target.value)))} className={cx(input, 'num w-28')} />
                    </td>
                    <td className={td}>
                      <span className="flex items-center gap-1.5">
                        <input aria-label="Cash reward" type="number" step="any" value={m.usd} onChange={(e) => set((d) => void (d.referral.milestones[i]!.usd = Number(e.target.value)))} className={cx(input, 'num w-32')} />
                        <span className="text-muted">$</span>
                      </span>
                    </td>
                    <td className={cx(td, 'text-right')}>
                      <button type="button" className={btn} onClick={() => set((d) => void d.referral.milestones.splice(i, 1))}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className={btn}
              disabled={s.referral.milestones.length >= 10}
              onClick={() =>
                set((d) => {
                  const last = d.referral.milestones.at(-1);
                  d.referral.milestones.push({ friends: last ? last.friends * 2 : 10, usd: last ? last.usd * 2 : 500 });
                })
              }
            >
              Add milestone
            </button>
            <span className="text-xs text-muted">List them from fewest friends to most. Clients see them as rungs under Friend milestones on the Referrals page.</span>
          </div>
        </Section>
      )}

      {section === 'tiers' && (
        <>
          <Section title="Leaderboard" sub="Sample traders: made-up names and points mixed in with real clients, changing every day at 00:00 UTC, to show how a busy leaderboard looks.">
            <label className="flex max-w-[360px] flex-col gap-1 text-[11px] text-muted">
              Sample traders
              <select
                id="earn-samples"
                value={s.leaderboard.samples}
                onChange={(e) => set((d) => void (d.leaderboard.samples = e.target.value as EarnSettings['leaderboard']['samples']))}
                className={input}
              >
                <option value="preview">Show to admins and demo accounts</option>
                <option value="off">Off</option>
              </select>
            </label>
            <p className="m-0 text-xs text-muted">Clients see sample traders only while they're on a demo account. On a live account the leaderboard shows real clients only.</p>
          </Section>

          <Section
            title="Tiers"
            sub="Reached by all-time live trading volume. The first tier must start at $0, and each tier needs more volume than the one before."
            aside={
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btn}
                  disabled={s.tiers.length >= 12}
                  onClick={() =>
                    set((d) => {
                      const last = d.tiers[d.tiers.length - 1]!;
                      d.tiers.push({ id: `tier_${Date.now().toString(36)}`, name: `Tier ${d.tiers.length + 1}`, minVolume: last.minVolume * 2 || 1000, cashbackPct: last.cashbackPct, multiplier: last.multiplier, color: '#8b919c', reachPoints: last.reachPoints });
                    })
                  }
                >
                  + Add tier
                </button>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-xs">
                <thead>
                  <tr>
                    {['', 'Name', 'From volume ($)', 'Cashback (%)', 'Points multiplier', 'Reach points', 'Colour', ''].map((h, i) => (
                      <th key={i} className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {s.tiers.map((t, i) => (
                    <tr key={i} className="border-t border-line">
                      <td className={td}>
                        <span className="grid size-6 place-items-center rounded-md text-[11px] font-bold text-black" style={{ background: t.color }}>
                          {t.name[0] ?? '?'}
                        </span>
                      </td>
                      <td className={td}>
                        <input aria-label="Tier name" value={t.name} maxLength={24} onChange={(e) => set((d) => void (d.tiers[i]!.name = e.target.value))} className={input} />
                      </td>
                      <td className={td}>
                        <input aria-label="From volume" type="number" disabled={i === 0} value={t.minVolume} onChange={(e) => set((d) => void (d.tiers[i]!.minVolume = Number(e.target.value)))} className={cx(input, 'num')} />
                      </td>
                      <td className={td}>
                        <input aria-label="Cashback percent" type="number" step="any" value={t.cashbackPct} onChange={(e) => set((d) => void (d.tiers[i]!.cashbackPct = Number(e.target.value)))} className={cx(input, 'num')} />
                      </td>
                      <td className={td}>
                        <input aria-label="Points multiplier" type="number" step="any" value={t.multiplier} onChange={(e) => set((d) => void (d.tiers[i]!.multiplier = Number(e.target.value)))} className={cx(input, 'num')} />
                      </td>
                      <td className={td}>
                        <input aria-label="Reach points" type="number" step="1" disabled={i === 0} value={t.reachPoints} onChange={(e) => set((d) => void (d.tiers[i]!.reachPoints = Number(e.target.value)))} className={cx(input, 'num')} />
                      </td>
                      <td className={td}>
                        <input aria-label="Colour" type="color" value={t.color} onChange={(e) => set((d) => void (d.tiers[i]!.color = e.target.value))} className="h-9 w-12 cursor-pointer rounded-lg border border-line bg-bg" />
                      </td>
                      <td className={td}>
                        <button type="button" className={btn} disabled={s.tiers.length <= 1 || i === 0} onClick={() => set((d) => void d.tiers.splice(i, 1))}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Points and quests" sub="Points rank clients on the leaderboard. Point changes apply to everyone straight away. Starter steps are shown to clients in this order.">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Num label="USDT of volume per point" suffix="$" value={s.pointsPerUsd} onChange={(v) => set((d) => void (d.pointsPerUsd = v))} />
              {QUESTS.map((q) => (
                <Num key={q.id} label={q.title} value={s.questPoints[q.id]} step="1" onChange={(v) => set((d) => void (d.questPoints[q.id] = v))} />
              ))}
            </div>
          </Section>
        </>
      )}

      {section === 'history' && (
        <Section title="History" sub="Each save is a new version, in effect from the time it was saved.">
          {data.history.length === 0 ? (
            <p className="m-0 text-xs text-muted">Using the built-in defaults. Nothing saved yet.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs">
              {data.history.map((h, i) => (
                <li key={h.id} className="flex gap-3">
                  <span className="num text-muted">{fmtDateTime(h.effectiveFrom, { year: true })}</span>
                  <span>{h.by ?? 'Unknown'}</span>
                  {i === 0 && <span className="rounded bg-up-bg px-1.5 text-up">Current</span>}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </div>
  );
}

// ---------- Yield ----------

type AdminPlan = YieldPlan & { stakers: number; interestPaid: string };
type PlanForm = { id: number | null; coin: YieldCoin; termDays: number; custom: boolean; apy: string; minStake: string; maxPerUser: string; capacity: string; label: string; enabled: boolean; sortOrder: number };

const toForm = (p: AdminPlan | null, coin: YieldCoin = 'BTC', termDays = 90): PlanForm =>
  p
    ? { id: p.id, coin: p.coin, termDays: p.termDays, custom: !YIELD_TERMS.some((t) => t.days === p.termDays), apy: p.apy, minStake: p.minStake, maxPerUser: p.maxPerUser, capacity: p.capacity, label: p.label, enabled: p.enabled, sortOrder: p.sortOrder }
    : { id: null, coin, termDays, custom: false, apy: '5', minStake: '100', maxPerUser: '250000', capacity: '1000000', label: '', enabled: true, sortOrder: 100 };

function PlanEditor({ form, onClose, onSaved }: { form: PlanForm; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState(form);
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  const up = (p: Partial<PlanForm>) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    setBusy(true);
    setE(null);
    try {
      const body = { coin: f.coin, termDays: f.termDays, apy: f.apy, minStake: f.minStake, maxPerUser: f.maxPerUser, capacity: f.capacity, label: f.label, enabled: f.enabled, sortOrder: f.sortOrder };
      if (f.id === null) await api.post('/api/admin/yield/plans', body);
      else await api.patch(`/api/admin/yield/plans/${f.id}`, body);
      onSaved();
    } catch (x) {
      setE(err(x));
      setBusy(false);
    }
  };
  const field = (label: string, key: 'apy' | 'minStake' | 'maxPerUser' | 'capacity', suffix?: string) => (
    <label className="flex flex-col gap-1 text-[11px] text-muted">
      {label}
      <span className="relative">
        <input inputMode="decimal" value={f[key]} onChange={(ev) => up({ [key]: ev.target.value } as Partial<PlanForm>)} className={cx(input, 'num', suffix && 'pr-8')} />
        {suffix && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs">{suffix}</span>}
      </span>
    </label>
  );
  return (
    <Modal open onClose={onClose} title={f.id === null ? 'New Yield plan' : `Edit plan · ${f.coin} ${termLabel(f.termDays)}`} width={480}>
      <div className="flex flex-col gap-3 text-xs">
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Coin
          <div className="flex gap-1.5">
            {YIELD_COINS.map((c) => (
              <button key={c} type="button" disabled={f.id !== null} aria-pressed={f.coin === c} onClick={() => up({ coin: c })} className={cx('h-8 rounded-full border px-3 font-semibold disabled:opacity-60', f.coin === c ? 'border-accent bg-accent text-white' : 'border-line-strong text-fg')}>
                {c}
              </button>
            ))}
          </div>
        </label>
        <div className="flex flex-col gap-1 text-[11px] text-muted">
          Lock period
          <div className="flex flex-wrap gap-1.5">
            {YIELD_TERMS.map((t) => (
              <button key={t.days} type="button" aria-pressed={!f.custom && f.termDays === t.days} onClick={() => up({ termDays: t.days, custom: false })} className={cx('h-8 rounded-full border px-3 font-semibold', !f.custom && f.termDays === t.days ? 'border-accent bg-accent text-white' : 'border-line-strong text-fg')}>
                {t.label}
              </button>
            ))}
            <button type="button" aria-pressed={f.custom} onClick={() => up({ custom: true, termDays: f.termDays || 60 })} className={cx('h-8 rounded-full border px-3 font-semibold', f.custom ? 'border-accent bg-accent text-white' : 'border-line-strong text-fg')}>
              Custom
            </button>
          </div>
          {f.custom && (
            <span className="relative mt-1">
              <input type="number" min={1} max={3650} value={f.termDays} onChange={(ev) => up({ termDays: Math.max(0, Math.round(Number(ev.target.value))) })} className={cx(input, 'num pr-12')} aria-label="Custom lock period in days" />
              <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">days</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field('APY', 'apy', '%')}
          {field('Min per stake (USDT)', 'minStake')}
          {field('Max per user (USDT)', 'maxPerUser')}
          {field('Total capacity (USDT)', 'capacity')}
          <label className="col-span-2 flex flex-col gap-1 text-[11px] text-muted">
            Label shown to clients
            <input value={f.label} maxLength={60} onChange={(ev) => up({ label: ev.target.value })} placeholder={f.termDays ? `Locked ${f.termDays} days` : 'Redeem any time'} className={input} />
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            Sort order
            <input type="number" value={f.sortOrder} onChange={(ev) => up({ sortOrder: Math.round(Number(ev.target.value)) })} className={cx(input, 'num')} />
          </label>
          <label className="flex items-center gap-2 self-end pb-2 text-xs text-fg">
            <input type="checkbox" checked={f.enabled} onChange={(ev) => up({ enabled: ev.target.checked })} className="size-4 accent-[var(--accent)]" />
            On, visible to clients
          </label>
        </div>
        <p className="m-0 text-muted">Changes apply to new stakes only. Clients who already staked keep their rate and unlock date. Turning a plan off hides it; existing stakes run to the end.</p>
        {e && <p className="m-0 text-down">{e}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primary} disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecideModal({ r, decision, onClose, onDone }: { r: UnlockRequest; decision: 'approve' | 'reject'; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} title={`${decision === 'approve' ? 'Approve early unlock' : 'Reject unlock request'} · ${clientLabel({ name: r.name, email: r.email, crmId: r.externalClientId })}`} width={440}>
      <div className="flex flex-col gap-3 text-xs">
        <p className="m-0 text-muted">
          {decision === 'approve'
            ? `${r.qty} ${r.coin} goes back to the client's live account in USDT at the current ${r.coin} price. The ${r.interest} ${r.coin} interest is forfeited. This can't be undone.`
            : 'The plan stays locked until its end date. The client sees your note.'}
        </p>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Note to the client {decision === 'reject' ? '(required)' : '(optional)'}
          <textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={3} maxLength={500} className={cx(input, 'h-auto py-2')} />
        </label>
        {e && <p className="m-0 text-down">{e}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || (decision === 'reject' && !note.trim())}
            className={cx('h-9 rounded-lg px-4 text-xs font-semibold text-white disabled:opacity-50', decision === 'approve' ? 'bg-accent' : 'bg-btn-short')}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/api/admin/yield/unlock-requests/${r.id}`, { decision, note });
                onDone();
              } catch (x) {
                setE(err(x));
                setBusy(false);
              }
            }}
          >
            {decision === 'approve' ? 'Approve and unlock' : 'Reject request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const CONTACT: Record<string, string> = { phone: 'Phone call', whatsapp: 'WhatsApp', email: 'Email' };

export function YieldAdminTab() {
  const plans = useGet<AdminPlan[]>('/api/admin/yield/plans');
  const [status, setStatus] = useState<'pending' | 'all'>('pending');
  const reqs = useGet<UnlockRequest[]>(`/api/admin/yield/unlock-requests${status === 'pending' ? '?status=pending' : ''}`);
  const [edit, setEdit] = useState<PlanForm | null>(null);
  const [decide, setDecide] = useState<{ r: UnlockRequest; decision: 'approve' | 'reject' } | null>(null);
  const list = plans.data ?? [];
  const terms = [...new Set([...YIELD_TERMS.map((t) => t.days), ...list.map((p) => p.termDays)])].sort((a, b) => a - b);
  const pending = (reqs.data ?? []).filter((r) => r.status === 'pending').length;

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={`Early unlock requests${pending ? ` · ${pending} waiting` : ''}`}
        sub="Clients ask to unlock a locked plan early. Each request also opens a support ticket and emails the support inbox."
        aside={
          <div className="flex gap-1">
            {(['pending', 'all'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)} className={cx(btn, status === s && 'border-accent bg-accent text-white')}>
                {s === 'pending' ? 'Waiting' : 'All'}
              </button>
            ))}
          </div>
        }
      >
        {!reqs.data ? (
          <Spinner />
        ) : reqs.data.length === 0 ? (
          <Empty>No {status === 'pending' ? 'waiting ' : ''}requests</Empty>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {reqs.data.map((r) => (
              <div key={r.id} className={cx('flex flex-col gap-2 rounded-xl border p-3 text-xs', r.status === 'pending' ? 'border-warn/50 bg-warn-bg' : 'border-line')}>
                <div className="flex flex-wrap items-center gap-2">
                  <ClientCell className="text-[13px]" name={r.name} email={r.email} crmId={r.externalClientId} />
                  <div className="flex-1" />
                  {r.ticketNumber && <span className="num rounded bg-track px-1.5 py-0.5">T-{r.ticketNumber}</span>}
                  <span className={cx('rounded px-1.5 py-0.5 font-semibold', r.status === 'pending' ? 'bg-warn text-black' : r.status === 'approved' ? 'bg-up-bg text-up' : 'bg-track text-muted')}>{r.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="text-muted">Plan</span>
                  <span className="num">
                    {r.coin} {termLabel(r.termDays)} · {r.qty} {r.coin}
                  </span>
                  <span className="text-muted">Unlocks on</span>
                  <span className="num">{r.unlockAt ? fmtDateTime(r.unlockAt, { year: true }).split(',')[0] : '—'}</span>
                  <span className="text-muted">Interest to forfeit</span>
                  <span className="num">
                    {r.interest} {r.coin}
                  </span>
                  <span className="text-muted">Reason</span>
                  <span>{r.reason}</span>
                  <span className="text-muted">Contact</span>
                  <span className="font-semibold">
                    {CONTACT[r.contactMethod]}
                    {r.phone ? ` · ${r.phone}` : ''}
                    {r.callbackWindow ? ` · ${r.callbackWindow}` : ''}
                  </span>
                  <span className="text-muted">Sent</span>
                  <span className="num">{fmtDateTime(r.createdAt, { year: true })}</span>
                </div>
                {r.message && <p className="m-0 rounded-lg bg-bg/60 px-2.5 py-2">“{r.message}”</p>}
                {r.note && <p className="m-0 text-muted">Note: {r.note}</p>}
                {r.status === 'pending' && (
                  <div className="flex justify-end gap-2">
                    <button type="button" className={btn} onClick={() => setDecide({ r, decision: 'reject' })}>
                      Reject
                    </button>
                    <button type="button" className={primary} onClick={() => setDecide({ r, decision: 'approve' })}>
                      Approve &amp; unlock
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Plans by coin"
        sub="Each cell is a plan: click it to edit, or click an empty cell to create one. Clients only see plans that are on."
        aside={
          <button type="button" className={primary} onClick={() => setEdit(toForm(null))}>
            + New plan
          </button>
        }
      >
        {!plans.data ? (
          plans.error ? <p className="m-0 text-xs text-down">{plans.error}</p> : <Spinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-xs">
              <thead>
                <tr>
                  <th className={th}>Coin</th>
                  {terms.map((t) => (
                    <th key={t} className={cx(th, 'text-right')}>
                      {termLabel(t)}
                    </th>
                  ))}
                  <th className={cx(th, 'text-right')}>Staked / capacity</th>
                </tr>
              </thead>
              <tbody>
                {YIELD_COINS.map((c) => {
                  const mine = list.filter((p) => p.coin === c);
                  const staked = mine.reduce((t, p) => t.add(p.staked), D.ZERO);
                  const cap = mine.reduce((t, p) => t.add(p.capacity), D.ZERO);
                  return (
                    <tr key={c} className="border-t border-line">
                      <td className={td}>
                        <span className="flex items-center gap-2 font-semibold">
                          <span className="grid size-5 place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: YIELD_COIN_INFO[c].color }}>
                            {YIELD_COIN_INFO[c].icon}
                          </span>
                          {c}
                        </span>
                      </td>
                      {terms.map((t) => {
                        const p = mine.find((x) => x.termDays === t);
                        return (
                          <td key={t} className={cx(td, 'text-right')}>
                            {p ? (
                              <button
                                type="button"
                                onClick={() => setEdit(toForm(p))}
                                title={`${fmtUsd(p.staked)} staked by ${p.stakers}`}
                                className={cx('num inline-flex flex-col items-end rounded-lg border px-2.5 py-1', p.enabled ? 'border-line-strong hover:border-accent' : 'border-dashed border-line text-muted')}
                              >
                                <span className="text-[13px] font-semibold">{Number(p.apy)}%</span>
                                <span className="text-[10.5px] text-muted">{p.enabled ? `${fmtUsd(p.staked)} · ${p.stakers}` : 'off'}</span>
                              </button>
                            ) : (
                              <button type="button" onClick={() => setEdit(toForm(null, c, t))} className="rounded-lg border border-dashed border-line px-2.5 py-1.5 text-muted hover:text-fg">
                                + Add
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td className={cx(td, 'num text-right')}>
                        {fmtUsd(staked.toString())} / {fmtUsd(cap.toString())}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {edit && (
        <PlanEditor
          form={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            void plans.reload();
          }}
        />
      )}
      {decide && (
        <DecideModal
          r={decide.r}
          decision={decide.decision}
          onClose={() => setDecide(null)}
          onDone={() => {
            setDecide(null);
            void reqs.reload();
            void plans.reload();
          }}
        />
      )}
    </div>
  );
}
